import type { ReactNode } from "react";

import { useState } from "react";

import { MenuTrigger, Popover } from "react-aria-components";

import { Autocomplete } from "../autocomplete";
import { FilterButton } from "./filter-button";
import { FilterContainer } from "./filter-container";
import { Placeholder } from "./placeholder";

import styles from "./async-filter.module.css";

type FilterProps<T extends { id: string }> = {
	dynPlaceholder?: ReactNode;
	emptyState?: ReactNode;
	fetchNextPage: () => void;
	hasNextPage: boolean;
	isFetching: boolean;
	isFetchingNextPage: boolean;
	items: T[];
	loadingNewPageItem: ReactNode;
	menuItem: (item: T) => ReactNode;
	onChange: (value: null | string) => void;
	placeholder: string;
	searchbar: ReactNode;
	searchItem?: ReactNode;
	selected: null | string;
};

/**
 * AsyncFilter is a filter component that fetches its options asynchronously.
 * It uses the Autocomplete component to display the options and handle the search functionality.
 */
export function AsyncFilter<T extends { id: string }>({
	dynPlaceholder,
	emptyState,
	fetchNextPage,
	hasNextPage,
	isFetching,
	isFetchingNextPage,
	items,
	loadingNewPageItem,
	menuItem,
	onChange,
	placeholder,
	searchbar,
	searchItem,
	selected,
}: FilterProps<T>): ReactNode {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<FilterContainer onChange={onChange} selected={selected}>
			<MenuTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
				<FilterButton isOpen={isOpen} placeholder={placeholder} selected={selected}>
					<Placeholder
						dynPlaceholder={dynPlaceholder}
						items={[]}
						placeholder={placeholder}
						selected={selected}
					/>
				</FilterButton>
				<Popover>
					<div className={styles.dropdown}>
						<Autocomplete
							emptyState={emptyState}
							fetchNextPage={fetchNextPage}
							hasNextPage={hasNextPage}
							isFetching={isFetching}
							isFetchingNextPage={isFetchingNextPage}
							items={items}
							loadingNewPageItem={loadingNewPageItem}
							menuItem={menuItem}
							onChange={(val) => {
								onChange(val);
								setIsOpen(false);
							}}
							searchbar={searchbar}
							searchItem={searchItem}
						/>
					</div>
				</Popover>
			</MenuTrigger>
		</FilterContainer>
	);
}
